import {useNavigate, useParams} from "react-router-dom";
import Speakers from "./Speakers/Speakers";
import ConferenceTile from "./ConferenceTile/ConferenceTile";
import {Box, Button, Grid, Media, Stack, ValidationMessage} from "@mbank-design/design-system/components";
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {AppContext} from "../../shared/AppContext";
import axios from "axios";
import {
    AdditionalSupport,
    AllowedParticipationType,
    Speaker,
    SpeakerInvitationCreateMessage,
    SpeakerInvitationDay
} from "../../model/SpeakerInvitationCreateMessage";
import {SpeakersContext} from "./Speakers/SpeakersContext";
import {InvitationDatesContext} from "./SpeakerInvitationDays/InvitationDatesContext";
import SpeakerInvitationDays from "./SpeakerInvitationDays/SpeakerInvitationDays";
import AdditionalSupportSection from "./AdditionalSupport/AdditionalSupportSection";
import {AdditionalSupportContext} from "./AdditionalSupport/AdditionalSupportContext";
import {ParticipationType} from "../../model/ParticipationType";
import SpeakerInvitationResponseMessage from "../../model/SpeakerInvitationResponseMessage";
import _ from "lodash";
import {LayoutContext, LayoutContextState} from "../LayoutContext";
import ConfirmSending, {SendConfirmationSendStatus} from "./ConfirmSending";
import {
    CHECK_AFTER_SPEAKER_REMOVED,
    CHECK_BEFORE_SEND,
    createDefaultValidationAction,
    DATE_CHANGED_ACTION,
    ValidationContext,
    ValidationContextDispatch
} from "./SpeakerInvitationValidation";
import SpeakerInvitationResponse, {SpeakerInvitationResponseStatus} from "../../model/SpeakerInvitationResponse";

export default function SpeakerInvitationForm() {
    const titlePrefix: string = 'R.S.V.P for';
    const { invitationId } = useParams();
    const appContext = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(false);
    const [invitation, setInvitation] = useState<SpeakerInvitationCreateMessage>();
    const [speakers, setSpeakers] = useState<Speaker[]>([]);
    const [dates, setDates] = useState<SpeakerInvitationDay[]>([]);
    const [additionalSupport, setAdditionalSupport] = useState<AdditionalSupport>(() => {
        const additionalSupport: AdditionalSupport = {
            showBanquet: true,
            banquetLocation: '',
            showHotel: true,
            showParking: true,
            parkingCount: 0,
            hotelCount: 0,
            banquetCount: 0
        };

        return additionalSupport;
    });

    const { changeTitle } = useContext<LayoutContextState>(LayoutContext);
    const [requestedToOpenSendConfirmation, setRequestedToOpenSendConfirmation] = useState<boolean>(false);
    const navigate = useNavigate();
    const validationState = useContext(ValidationContext);
    const validationDispatch = useContext(ValidationContextDispatch);

    const isFormValid = (validationState.isInputSectionValid || validationState.isInputSectionValid === undefined)
        && (validationState.isDatesSectionValid || validationState.isDatesSectionValid === undefined);
    
    const isFormValidationUndefined = validationState.isInputSectionValid === undefined || validationState.isDatesSectionValid === undefined;

    const validateAfterParticipationTypeChange = useCallback((day: SpeakerInvitationDay) => {
        setRequestedToOpenSendConfirmation(false);
        validationDispatch!({
            ...createDefaultValidationAction(),
            type: DATE_CHANGED_ACTION,
            changedDay: day
        });
    }, [validationDispatch]);
    
    const validateAfterSlotChange = useCallback((day: SpeakerInvitationDay) => {
        setRequestedToOpenSendConfirmation(false);
        validationDispatch!({
            ...createDefaultValidationAction(),
            type: DATE_CHANGED_ACTION,
            changedDay: day
        });
    },[validationDispatch]);
    
    const validateAfterSpeakerRemoved = useCallback((speakers: Speaker[]) => {
        setRequestedToOpenSendConfirmation(false);
        validationDispatch!({
           ...createDefaultValidationAction(),
           type: CHECK_AFTER_SPEAKER_REMOVED,
           speakers 
        });
    }, [validationDispatch] );

    const validateForm = useCallback(() => {
        setRequestedToOpenSendConfirmation(false);
        validationDispatch!({
            ...createDefaultValidationAction(),
            type: CHECK_BEFORE_SEND,
            days: dates,
            speakers: speakers
        });
    }, [validationDispatch, validationState, dates, speakers]);

    const onParticipationTypeChange = (date: Date, participationType: ParticipationType) => {
        const modifiedDates = dates.map(d => {
            if (d.date === date) {
                d.participationType = participationType;
                if (participationType === ParticipationType.NotParticipate) {
                    d.slots.forEach(s => {
                        s.checked = false;
                        s.disabled = true;
                    });
                } else {
                    d.slots.forEach(s => s.disabled = false);
                }

                validateAfterParticipationTypeChange(d);
            }
            return d;
        });
        
        setDates(modifiedDates);
    }
    
    const onSpeakerChange = (speaker: Speaker) => {
        const modifiedSpeakers = speakers.map((s) => {
            if (s.uiId === speaker.uiId) {
                s.firstName = speaker.firstName;
                s.lastName = speaker.lastName;
                s.email = speaker.email;
            }
            
            return s;
        });
        
        setSpeakers(modifiedSpeakers);
    }
    
    const onSlotChange = (slotDate: Date, uiId: number) => {
        const modifiedDates = dates.map(d => {
            if (d.date === slotDate) {
                d.slots.forEach(s => {
                    if (s.uiId === uiId) {
                        s.checked = !s.checked;
                        validateAfterSlotChange(d);
                    }
                    return s;
                });
            }
            return d;
        });
        
        setDates(modifiedDates);
    };

    const onAddSpeaker = () => {
        const maxUUId = _.maxBy(speakers, (s: Speaker) => s.uiId)?.uiId ?? 0;
        const maxOrder = _.maxBy(speakers, (s: Speaker) => s.order)?.order ?? 0;
        const newSpeakers = [...speakers, { firstName: '', lastName: '', email: '', uiId: maxUUId + 1, order: maxOrder + 1}];

        setSpeakers(newSpeakers);
    }

    const onSpeakerRemove = (uiId: number) => {
        let newSpeakersList: Speaker[] = [];
        
        if (speakers.length > 1) {
            newSpeakersList = speakers.filter(s => s.uiId !== uiId);
        } else {
            newSpeakersList = [ { firstName: '', lastName: '', email: '', uiId: 0, order: 0}];
        }
        
        setSpeakers(newSpeakersList);
        validateAfterSpeakerRemoved(newSpeakersList);
    }

    const onParkingCountChange = ((count: number) => {
        setAdditionalSupport({...additionalSupport, parkingCount: count});
    })

    const onHotelCountChange = ((count: number) => {
        setAdditionalSupport({...additionalSupport, hotelCount: count});
    })

    const onBanquetCountChange = ((count: number) => {
        setAdditionalSupport({...additionalSupport, banquetCount: count});
    })
    
    const openSendConfirmationDialog = () => {
        validateForm();
        
        if(!isFormValidationUndefined) {
            setRequestedToOpenSendConfirmation(true);
        }
    }

    const onSendConfirmationClose = (status: SendConfirmationSendStatus) => {
        setRequestedToOpenSendConfirmation(false);
        if (status === SendConfirmationSendStatus.Send) {
            sendForm();
        }
    }
    
    const sendForm = () => {
        const message = createResponseMessage();
        const requestUrl = `${appContext.apiUrl}/speaker-invitations/response?code=${appContext.functionCode}`;
        
        const handle = async () => {
            await axios.post(requestUrl, message);
        }

        handle()
            .then(() => navigate('/speaker/invitation/sent'));
    }
    
    const calculateInitialParticipationType = (allowedParticipationType: AllowedParticipationType): ParticipationType => {
        switch (allowedParticipationType) {
            case AllowedParticipationType.Hybrid:
                return ParticipationType.OnSite;
            case AllowedParticipationType.OnSite:
                return ParticipationType.OnSite;
            case AllowedParticipationType.Remote:
                return ParticipationType.Remote;
            default: 
                return ParticipationType.OnSite;
        }
    }
    
    const createResponseMessage = () : SpeakerInvitationResponseMessage => {
        return {
            invitationId: invitationId!,
            speakers: speakers.map((s) => {
                return {
                    firstName: s.firstName,
                    lastName: s.lastName,
                    email: s.email
                }
            }),
            conferenceDates: dates.map((d) => {
                return {
                    date: d.date,
                    slots: d.slots.filter((s) => s.checked).map((s) => {
                        return {
                          timeFrom: s.timeFrom,
                          timeTo: s.timeTo  
                        };
                    }),
                    participationType: d.participationType!
                }
            }),
            banquetCount: additionalSupport.banquetCount,
            parkingCount: additionalSupport.parkingCount,
            hotelCount: additionalSupport.hotelCount
        };
    }

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            const request = `${appContext.apiUrl}/speaker-invitations/${invitationId}?code=${appContext.functionCode}`;
            const response = await axios<SpeakerInvitationResponse>(request);

            switch (response.data.status)
            {
                case SpeakerInvitationResponseStatus.NotFound:
                    navigate('/');
                    return;
                case SpeakerInvitationResponseStatus.Cancelled:
                    navigate('/speaker/invitation/cancelled');
                    return;
                case SpeakerInvitationResponseStatus.Sent:
                    navigate('/speaker/invitation/alreadySubmitted');
                    return;
                case SpeakerInvitationResponseStatus.Expired:
                    navigate('/speaker/invitation/expired');
                    return;
                default:
                    break;
            }
            const result = response.data.body;
            
            setInvitation(result);
            
            setSpeakers(result.speakers.map((s,i) => {
                return {...s, uiId: i, isValid: true};
            }));
            
            setDates(result.conferenceDates.map(d => {
              return { 
                  date: d.date, 
                  participationType: calculateInitialParticipationType(d.allowedParticipationType), 
                  allowedParticipationType: d.allowedParticipationType,
                slots: d.slots.map((s,i) => {
                    return {...s, uiId: i, checked: false, disabled: false};
                })  
              };  
            }));

            setAdditionalSupport(() => {
                const additionalSupport: AdditionalSupport = {
                    showBanquet: result.banquet != null,
                    banquetLocation: result.banquet?.banquetLocation.name,
                    showHotel: result.hotel,
                    showParking: result.parking,
                    parkingCount: 0,
                    hotelCount: 0,
                    banquetCount: 0
                };

                return additionalSupport;
            });
            
            if (changeTitle !== undefined)
                changeTitle(`${titlePrefix} ${result.conferenceName}`);
        }

        if (appContext?.apiUrl?.length > 0) {
            fetchData()
                .then(() => setIsLoading(false))
                .catch((error) => {
                    navigate('/');
                });
        }
    }, [invitationId, appContext.apiUrl, appContext.functionCode]);

    const speakersContextValue = useMemo(() => ({
        speakers,
        onSpeakerChange,
        onAddSpeaker,
        onSpeakerRemove
    }), [speakers, onSpeakerChange, onAddSpeaker, onSpeakerRemove]);
    
    const datesContextValue = useMemo(() => ({
        dates,
        onSlotChange,
        onParticipationTypeChange
    }), [dates, onSlotChange, onParticipationTypeChange]);

    const additionalSupportContextValue = useMemo(() => ({
        ...additionalSupport,
        onParkingCountChange,
        onHotelCountChange,
        onBanquetCountChange
    }), [additionalSupport, onParkingCountChange, onHotelCountChange, onBanquetCountChange]);
    
    return(
        <>{ !isLoading && invitation ? (
            <Stack>
                <Box paddingTop={32}>
                    <Grid>
                        <Grid.Row>
                            <Grid.Column span={[12,6]}>
                                <Box maxWidth={642}>
                                    <Media.MobileOnly>
                                        <Box marginBottom={32}>
                                            <ConferenceTile invitation={invitation!} />
                                        </Box>
                                    </Media.MobileOnly>
                                    <Stack>
                                        <SpeakersContext.Provider value={speakersContextValue}>
                                            <Speakers />    
                                        </SpeakersContext.Provider>
                                        <InvitationDatesContext.Provider value={datesContextValue}>
                                            <SpeakerInvitationDays  />
                                        </InvitationDatesContext.Provider>
                                        <AdditionalSupportContext.Provider value={additionalSupportContextValue}>
                                            <AdditionalSupportSection />
                                        </AdditionalSupportContext.Provider>
                                        <Box marginTop={16}>
                                            <Stack direction={"row"} alignY={"center"}>
                                                <Button onClick={() => openSendConfirmationDialog()} disabled={!isFormValid}>Send response</Button>
                                                <ValidationMessage>
                                                    {isFormValid ? '' : 'the form is not valid, please check and fix mistakes'}
                                                </ValidationMessage>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Grid.Column>
                            <Media.DesktopOnly>
                                <Grid.Column span={5} offset={1}>
                                    <ConferenceTile invitation={invitation!} />    
                                </Grid.Column>
                            </Media.DesktopOnly>
                        </Grid.Row>
                    </Grid>
                </Box>
            </Stack>
            
        ) : (
            <span>Loading...</span>
        )}
            <ConfirmSending isOpen={requestedToOpenSendConfirmation && isFormValid} onClose={onSendConfirmationClose} />
        </>
    )
}